import { useGlobalState } from '@/stores/globalState'
import type { ChatContextPayload } from '@/lib/chatApi'

/**
 * Structured app context sent with every /api/chat request. The backend
 * builds the system prompt from this (and gates the add_game tool on
 * guideId being present + no generation running).
 */
export function useChatContext(): ChatContextPayload {
  const activeModule = useGlobalState((s) => s.activeModule)
  const activeWorkspaceItem = useGlobalState((s) => s.activeWorkspaceItem)
  const workspaceView = useGlobalState((s) => s.workspaceView)
  const activeLearner = useGlobalState((s) => s.activeLearner)

  // activeWorkspaceItem doubles as guide id in the guides module; special
  // sentinel values ('new', 'evaluation-flow', ...) are not guides.
  const isGuideOpen =
    activeModule === 'MY_GUIDES' &&
    !!activeWorkspaceItem &&
    !['new', 'evaluation-flow', 'evaluation-results'].includes(activeWorkspaceItem)

  return {
    module: activeModule,
    view: workspaceView,
    ...(isGuideOpen ? { guideId: activeWorkspaceItem! } : {}),
    ...(activeLearner
      ? {
          learnerId: activeLearner.id,
          learnerName: activeLearner.name,
          learnerStage: activeLearner.stageLabel ?? undefined,
        }
      : {}),
    // Always report false — the FE queues add_game actions received during
    // an active build and auto-dispatches them once it finishes. Telling the
    // AI that generation is running causes it to withhold the action entirely,
    // promising "I'll do it after" without any mechanism to follow through.
    generationInProgress: false,
  }
}

/**
 * Per-guide chat threads (HOP-10): the active thread is keyed by the open
 * guide, or a per-learner platform thread anywhere else.
 */
export function useChatThreadKey(): string {
  const context = useChatContext()
  const activeLearnerId = useGlobalState((s) => s.activeLearnerId)
  return context.guideId ?? `platform:${activeLearnerId ?? 'anon'}`
}

/**
 * Ask Hopscotch context: same as useChatContext, but honours the context
 * pill's × (companionContextDetached) — while detached, the open guide is
 * omitted, so the thread key naturally falls back to the platform thread,
 * prompts go general, and new backend sessions carry no guideId.
 */
export function useCompanionContext(): ChatContextPayload {
  const context = useChatContext()
  const detached = useGlobalState((s) => s.companionContextDetached)
  if (!detached || !context.guideId) return context
  const { guideId: _guideId, ...rest } = context
  void _guideId
  return rest
}

export function useCompanionThreadKey(): string {
  const context = useCompanionContext()
  const activeLearnerId = useGlobalState((s) => s.activeLearnerId)
  return context.guideId ?? `platform:${activeLearnerId ?? 'anon'}`
}

/**
 * Where the companion must not exist at all (no handle, no panel): while a
 * game is being played full-screen, and during the evaluation flow. Streams
 * are never aborted by suppression — thread writes are key-captured, so
 * replies land (and light the unread dot) while hidden.
 */
export function useCompanionSuppressed(): boolean {
  const playingGameId = useGlobalState((s) => s.playingGameId)
  const activeModule = useGlobalState((s) => s.activeModule)
  const activeWorkspaceItem = useGlobalState((s) => s.activeWorkspaceItem)
  const evaluationInProgress = useGlobalState((s) => s.evaluation.inProgress)
  // The upload flow is a full-page focused task (own chrome) — hide the
  // companion while it's open, same as game play / evaluation.
  const uploadEntry = useGlobalState((s) => s.uploadEntry)
  return (
    playingGameId !== null ||
    uploadEntry !== null ||
    (activeModule === 'EVALUATION' &&
      (activeWorkspaceItem === 'evaluation-flow' || evaluationInProgress))
  )
}

/**
 * Builds the context label shown in the chat header
 * ("Currently viewing: ...")
 */
export function useChatContextLabel(): string {
  const activeModule = useGlobalState((s) => s.activeModule)
  const activeWorkspaceItem = useGlobalState((s) => s.activeWorkspaceItem)
  const activeLearner = useGlobalState((s) => s.activeLearner)
  const learningJourney = useGlobalState((s) => s.learningJourney)
  const mode = useGlobalState((s) => s.mode)

  if (activeModule === 'LEARNING_JOURNEY') {
    if (mode === 'EDUCATOR') return 'Educator Canvas'
    const activeWeek = learningJourney.weeklyHistory.find(
      (w) => w.status === 'active'
    )
    if (activeWeek) {
      return `Week ${activeWeek.weekNumber} – ${activeWeek.topic || activeWeek.skill}`
    }
    return 'Learning Journey'
  }

  if (activeModule === 'EVALUATION') {
    if (activeWorkspaceItem === 'evaluation-results' && activeLearner) {
      return `Evaluation Results – ${activeLearner.name}`
    }
    if (activeWorkspaceItem === 'evaluation-flow') {
      return 'Evaluation in Progress'
    }
    return 'Evaluation'
  }

  if (activeModule === 'MY_GUIDES') {
    if (activeWorkspaceItem && activeWorkspaceItem !== 'new') {
      return activeWorkspaceItem
    }
    return 'My Guides'
  }

  if (activeModule === 'BOOKS') {
    return 'Books'
  }

  return 'Hopscotch Hub'
}

/**
 * Builds the system prompt sent to the AI based on current app context.
 * This dynamically changes as the user navigates.
 */
export function useChatSystemPrompt(): string {
  const activeModule = useGlobalState((s) => s.activeModule)
  const activeWorkspaceItem = useGlobalState((s) => s.activeWorkspaceItem)
  const activeLearner = useGlobalState((s) => s.activeLearner)
  const learningJourney = useGlobalState((s) => s.learningJourney)
  const mode = useGlobalState((s) => s.mode)

  const base =
    'You are Hopscotch AI Guide, helping parents and educators with literacy instruction based on Science of Reading.'

  // Learning Journey context
  if (activeModule === 'LEARNING_JOURNEY') {
    if (mode === 'EDUCATOR') {
      return `${base}\n\nContext: User is in Educator Mode, building a custom canvas of games, stories, and activities. Help with content ideas, skill targeting, and activity suggestions.`
    }

    const activeWeek = learningJourney.weeklyHistory.find(
      (w) => w.status === 'active'
    )
    if (activeWeek) {
      const gameNames = activeWeek.games.map((g) => g.name).join(', ')
      const learnerInfo = activeLearner
        ? `Learner: ${activeLearner.name} (${activeLearner.stageLabel ?? 'unknown stage'}).`
        : ''

      return `${base}\n\nContext: User is on Week ${activeWeek.weekNumber} of Learning Journey. ${learnerInfo} Focus: ${activeWeek.skill}. Topic: ${activeWeek.topic}. Story: "${activeWeek.story.title}". Games: ${gameNames}.\n\nHelp with: this week's content, extension activities, practice planning.`
    }

    return `${base}\n\nContext: User is viewing the Learning Journey module.`
  }

  // Evaluation context
  if (activeModule === 'EVALUATION') {
    if (
      activeWorkspaceItem === 'evaluation-results' &&
      activeLearner?.evaluationResults
    ) {
      const results = activeLearner.evaluationResults
      const observed = Object.entries(results.skillsObserved)
        .filter(([, v]) => v)
        .map(([k]) => k.replace(/([A-Z])/g, ' $1').trim())
        .join(', ')
      const struggling =
        results.skillsStruggling.length > 0
          ? results.skillsStruggling.join(', ')
          : 'none identified'

      return `${base}\n\nContext: Evaluation completed for ${activeLearner.name}. Results: ${results.stageLabel}. Skills observed: ${observed || 'none yet'}. Struggling with: ${struggling}.\n\nHelp with: results interpretation, next steps, skill explanations.`
    }

    return `${base}\n\nContext: User is in the Evaluation module.`
  }

  // My Guides context (was READING_GUIDES)
  if (activeModule === 'MY_GUIDES') {
    if (activeWorkspaceItem && activeWorkspaceItem !== 'new') {
      return `${base}\n\nContext: User is viewing a Reading Guide. Help with: questions about the guide, usage suggestions, troubleshooting.`
    }
    return `${base}\n\nContext: User is browsing My Guides. Help with: finding guides, understanding skill levels, guide recommendations.`
  }

  // Books / Library context
  if (activeModule === 'BOOKS') {
    return `${base}\n\nContext: User is browsing the Books library — curated and uploaded titles with per-learner fit. Help with: finding books for the active learner, explaining fit categories, generating a guide from a book.`
  }

  // Generic fallback
  return base
}

/**
 * Returns a context key that changes when the context changes significantly.
 * Used to detect context changes in the chat.
 */
export function useChatContextKey(): string {
  const activeModule = useGlobalState((s) => s.activeModule)
  const activeWorkspaceItem = useGlobalState((s) => s.activeWorkspaceItem)
  const mode = useGlobalState((s) => s.mode)
  return `${activeModule}:${activeWorkspaceItem ?? ''}:${mode}`
}
